import { hostsAtom } from "@sunrise/http-client";
import { actionJWTSetTokens, jwtAtom, selectIsLoggedIn } from "@sunrise/jwt";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { isNil } from "lodash";
import { useEffect } from "react";

import { checkIfApproved, pinAtomQuery } from "./qr-code-login.service";
import { endpoints, type PinResponse } from "@sunrise/backend-types";

export type UseLoginReturn = {
  pin: PinResponse;
};

export function useQrCodeLogin(
  checkForApprovalMsInterval = 5000,
): UseLoginReturn {
  const queryClient = useQueryClient();
  const dispatchJWT = useSetAtom(jwtAtom);
  const isLoggedIn = useAtomValue(selectIsLoggedIn);
  const pinResponse = useAtomValue(pinAtomQuery);
  const hosts = useAtomValue(hostsAtom);
  const host = hosts.api;

  if (isNil(host)) throw new Error("Missing host");

  useEffect(() => {
    if (isLoggedIn) return;

    const onApprove = (resp: PinResponse): void => {
      window.clearInterval(id);

      if (!resp.access_token) throw new Error("Missing access token");
      if (!resp.refresh_token) throw new Error("Missing refresh token");

      dispatchJWT(
        actionJWTSetTokens({
          accessToken: resp.access_token,
          refreshToken: resp.refresh_token,
        }),
      );
    };

    const id = window.setInterval(() => {
      void checkIfApproved(
        endpoints.postPin(host, pinResponse.data.id),
        queryClient,
        onApprove,
      );
    }, checkForApprovalMsInterval);

    return () => {
      window.clearInterval(id);
    };
  }, [dispatchJWT, isLoggedIn, pinResponse, queryClient]);

  return { pin: pinResponse.data };
}
